import { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "../../supabase/supabaseClient";
import {
    listConversations,
    // createConversation,
    acceptConversation,
    declineConversation,
    listMessages,
    sendMessage,
    markConversationRead,
    listUnreadByConversation,
} from "../../services/messagesService";
import "./Messages.css";

export default function Messages() {
    const location = useLocation();
    const navigate = useNavigate();
    const messagesListRef = useRef(null);
    const [user, setUser] = useState(null);
    const [conversations, setConversations] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [composer, setComposer] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [nameMap, setNameMap] = useState({});
    const [unreadMap, setUnreadMap] = useState({});
    const [lastMessageMap, setLastMessageMap] = useState({});
    const [sharedPostMap, setSharedPostMap] = useState({});
    const [isMobile, setIsMobile] = useState(() => {
        if (typeof window === "undefined") return false;
        return window.innerWidth <= 900;
    });

    useEffect(() => {
        const init = async () => {
            const { data } = await supabase.auth.getSession();
            const currentUser = data?.session?.user ?? null;
            setUser(currentUser);
            if (!currentUser) {
                setLoading(false);
                setError("You must be signed in to view messages.");
                return;
            }
            await refreshConversations(currentUser.id, location.state?.conversationId);
            setLoading(false);
        };
        init(); /* goofy error */
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state]);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 900);
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // Auto-scroll to bottom on conversations
    useEffect(() => {
        if (messagesListRef.current) {
            messagesListRef.current.scrollTop = messagesListRef.current.scrollHeight;
        }
    }, [messages]);

    const extractSharedPostId = (body) => {
        if (!body) return null;
        const markerMatch = body.match(/\[shared_post:([^\]]+)\]/i);
        if (markerMatch?.[1]) return markerMatch[1].trim();
        const legacyMatch = body.match(/\(post id:\s*([^)]+)\)/i);
        if (legacyMatch?.[1]) return legacyMatch[1].trim();
        return null;
    };

    const extractOrgIdFromPostBody = (body) => {
        if (!body) return null;
        const markerMatch = body.match(/\n\n\[posted_as_org:([^\]]+)\]\s*$/i);
        return markerMatch?.[1]?.trim() || null;
    };

    const formatMessageBody = (body) => {
        if (!body) return "";
        return body
            .replace(/\n\n\[shared_post:[^\]]+\]\s*$/i, "")
            .replace(/\n\n\[posted_as_org:[^\]]+\]\s*$/i, "")
            .replace(/\n\n\(post id:[^)]+\)\s*$/i, "")
            .trimEnd();
    };

    const hydrateSharedPosts = async (messageRows) => {
        const ids = Array.from(new Set((messageRows || [])
            .map((m) => extractSharedPostId(m.body))
            .filter(Boolean)));

        const missingIds = ids.filter((id) => !sharedPostMap[id]);
        if (missingIds.length === 0) return;

        const { data: posts, error: postsErr } = await supabase
            .from("posts")
            .select("id, author_id, body, created_at, post_media(id, media_type, url, position)")
            .in("id", missingIds)
            .is("deleted_at", null);

        if (postsErr || !posts) return;

        const orgAuthorIds = Array.from(new Set(posts.map((p) => extractOrgIdFromPostBody(p.body)).filter(Boolean)));
        const authorIds = Array.from(new Set(posts
            .filter((p) => !extractOrgIdFromPostBody(p.body))
            .map((p) => p.author_id)
            .filter(Boolean)));
        const authorMap = {};
        const orgNameMap = {};

        if (authorIds.length > 0) {
            const { data: userProfiles } = await supabase
                .from("user_profiles")
                .select("user_id, first_name, last_name")
                .in("user_id", authorIds);

            (userProfiles || []).forEach((profile) => {
                const fullName = `${profile.first_name || ""} ${profile.last_name || ""}`.trim();
                if (fullName) authorMap[profile.user_id] = fullName;
            });

            const { data: playerProfiles } = await supabase
                .from("player_profiles")
                .select("user_id, firstname, lastname")
                .in("user_id", authorIds);

            (playerProfiles || []).forEach((profile) => {
                if (!authorMap[profile.user_id]) {
                    const fullName = `${profile.firstname || ""} ${profile.lastname || ""}`.trim();
                    if (fullName) authorMap[profile.user_id] = fullName;
                }
            });
        }

        if (orgAuthorIds.length > 0) {
            const { data: orgProfiles } = await supabase
                .from("org_profiles")
                .select("id, name")
                .in("id", orgAuthorIds);

            (orgProfiles || []).forEach((org) => {
                if (org?.id) orgNameMap[org.id] = org.name || "Organization";
            });
        }

        const mapped = {};
        (posts || []).forEach((post) => {
            const orgId = extractOrgIdFromPostBody(post.body);
            const media = Array.isArray(post.post_media)
                ? [...post.post_media].sort((a, b) => (a.position || 0) - (b.position || 0))
                : [];

            mapped[post.id] = {
                id: post.id,
                body: formatMessageBody(post.body || ""),
                created_at: post.created_at,
                authorName: orgId ? (orgNameMap[orgId] || "Organization") : (authorMap[post.author_id] || "User"),
                media,
            };
        });

        setSharedPostMap((prev) => ({ ...prev, ...mapped }));
    };

    const refreshConversations = async (userId, preferredId) => {
        const { data, error: convError } = await listConversations(userId);
        if (!convError && data) {
            setConversations(data);
            console.log("refreshConversations: loaded conversations", data.map(c => c.id));
            await hydrateNames(data, userId);
            // Diagnostic: re-query any names that are currently fallbacks
            await forceCheckFallbacks(data, userId);
            await refreshUnreadCounts(data, userId);
            await fetchLastMessages(data);
            const targetId = preferredId || selectedId;
            if (targetId) {
                setSelectedId(targetId);
                await loadMessages(targetId, userId);
            }
        }
    };

    const fetchLastMessages = async (convs) => {
        const lastMsgs = {};
        await Promise.all(convs.map(async (c) => {
            const { data } = await supabase
                .from("messages")
                .select("body, sender_id, created_at")
                .eq("conversation_id", c.id)
                .order("created_at", { ascending: false })
                .limit(1)
                .maybeSingle();
            if (data) lastMsgs[c.id] = data;
        }));
        setLastMessageMap(lastMsgs);
    };

    const refreshUnreadCounts = async (convs, currentUserId) => {
        const ids = convs.map((c) => c.id);
        const { data, error: unreadErr } = await listUnreadByConversation(currentUserId, ids);
        if (unreadErr || !data) return;
        const counts = data.reduce((acc, row) => {
            acc[row.conversation_id] = (acc[row.conversation_id] || 0) + 1;
            return acc;
        }, {});
        setUnreadMap(counts);
    };

    const hydrateNames = async (convs, currentUserId) => {
        const ids = Array.from(new Set(convs.map((c) => (c.initiator_id === currentUserId ? c.recipient_id : c.initiator_id)))).filter((id) => !nameMap[id]);
        console.log("hydrateNames: ids to resolve", ids, "existingNameMapKeys", Object.keys(nameMap));
        if (ids.length === 0) return;
        const entries = await Promise.all(ids.map(async (id) => {
            let display = null;
            console.log("a b c");
                // Try user_profile (maybeSingle returns object)
                try {
                    const { data: profile, error: profileErr } = await supabase
                        .from("user_profiles")
                        .select("first_name, last_name")
                        .eq("user_id", id)
                        .maybeSingle();
                    console.log("hydrateNames: user_profiles", { id, profileErr, profile });
                    if (!profileErr && profile && (profile.first_name || profile.last_name)) {
                        display = `${profile.first_name || ""} ${profile.last_name || ""}`.trim();
                    } else {
                        console.log("hi" + profile);
                    }
                } catch (e) {
                    console.debug("hydrateNames: user_profiles lookup failed", e);
                }

                // Fallback to player_profiles if not found in user_profiles
                if (!display) {
                    try {
                        const { data: player, error: playerErr } = await supabase
                            .from("player_profiles")
                            .select("firstname, lastname")
                            .eq("user_id", id)
                            .maybeSingle();
                        console.log("hydrateNames: player_profiles", { id, playerErr, player });
                        if (!playerErr && player && (player.firstname || player.lastname)) {
                            display = `${player.firstname || ""} ${player.lastname || ""}`.trim();
                        }
                    } catch (e) {
                        console.debug("hydrateNames: player_profiles lookup failed", e);
                    }
                }

            const fallback = id.slice(0, 8);
            return [id, display || fallback];
        }));
        setNameMap((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
    };

    // Diagnostic helper: force re-query names even if a fallback exists in nameMap
    const forceCheckFallbacks = async (convs, currentUserId) => {
        try {
            const otherIds = Array.from(new Set(convs.map((c) => (c.initiator_id === currentUserId ? c.recipient_id : c.initiator_id))));
            const suspects = otherIds.filter((id) => {
                const v = nameMap[id];
                return !v || v === (id ? id.slice(0, 8) : "");
            });
            if (suspects.length === 0) {
                console.log("forceCheckFallbacks: no suspect ids");
                return;
            }
            console.log("forceCheckFallbacks: re-querying", suspects);
            await Promise.all(suspects.map(async (id) => {
                try {
                    const { data: profile, error: profileErr } = await supabase
                        .from("user_profiles")
                        .select("first_name, last_name")
                        .eq("user_id", id)
                        .maybeSingle();
                    console.log("forceCheckFallbacks: user_profiles", { id, profileErr, profile });
                    if (!profileErr && profile && (profile.first_name || profile.last_name)) {
                        const display = `${profile.first_name || ""} ${profile.last_name || ""}`.trim();
                        setNameMap((prev) => ({ ...prev, [id]: display }));
                        return;
                    }

                    const { data: player, error: playerErr } = await supabase
                        .from("player_profiles")
                        .select("firstname, lastname")
                        .eq("user_id", id)
                        .maybeSingle();
                    console.log("forceCheckFallbacks: player_profiles", { id, playerErr, player });
                    if (!playerErr && player && (player.firstname || player.lastname)) {
                        const display = `${player.firstname || ""} ${player.lastname || ""}`.trim();
                        setNameMap((prev) => ({ ...prev, [id]: display }));
                        return;
                    }

                    console.log("forceCheckFallbacks: no profile found for id", id);
                } catch (e) {
                    console.error("forceCheckFallbacks exception for id", id, e);
                }
            }));
        } catch (e) {
            console.error("forceCheckFallbacks failed", e);
        }
    };

    const loadMessages = async (conversationId, viewerId = user?.id) => {
        if (!conversationId || !viewerId) return;
        const { data, error: msgError } = await listMessages(conversationId);
        if (!msgError && data) {
            setMessages(data);
            await hydrateSharedPosts(data);
            await markConversationRead(conversationId, viewerId);
            setUnreadMap((prev) => ({ ...prev, [conversationId]: 0 }));
        }
    };

    const handleSend = async () => {
        if (!composer.trim() || !selectedId || !user) return;
        const { data, error: sendErr } = await sendMessage(selectedId, user.id, composer.trim());
        if (!sendErr && data) {
            setMessages((prev) => [...prev, data]);
            setComposer("");
            await refreshConversations(user.id);
        }
    };

    const selectedConversation = conversations.find((c) => c.id === selectedId);
    const isRecipient = selectedConversation && selectedConversation.recipient_id === user?.id;
    const isPending = selectedConversation && selectedConversation.status === "pending";

    if (loading) {
        return (
            <main className="page">
                <div className="messages-shell">
                    <div className="conversations-list messages-skeleton-list">
                        <div className="conversations-header messages-skeleton-line messages-skeleton-line-title" />
                        {[1, 2, 3, 4, 5].map((i) => (
                            <div key={i} className="messages-skeleton-pill">
                                <div className="messages-skeleton-line messages-skeleton-line-name" />
                                <div className="messages-skeleton-line messages-skeleton-line-preview" />
                            </div>
                        ))}
                    </div>

                    <div className="messages-panel messages-skeleton-panel">
                        <div className="messages-skeleton-header">
                            <div className="messages-skeleton-line messages-skeleton-line-heading" />
                        </div>
                        <div className="messages-skeleton-message-list">
                            {[1, 2, 3, 4].map((i) => (
                                <div key={i} className={`messages-skeleton-bubble ${i % 2 === 0 ? "out" : "in"}`}>
                                    <div className="messages-skeleton-line messages-skeleton-line-bubble" />
                                    <div className="messages-skeleton-line messages-skeleton-line-time" />
                                </div>
                            ))}
                        </div>
                        <div className="messages-skeleton-composer">
                            <div className="messages-skeleton-line messages-skeleton-line-input" />
                            <div className="messages-skeleton-line messages-skeleton-line-send" />
                        </div>
                    </div>
                </div>
            </main>
        );
    }

    if (error) {
        return <main className="page"><div className="messages-shell">{error}</div></main>;
    }

    return (
        <main className="page">
            <div className={`messages-shell ${isMobile ? "messages-shell-mobile" : ""}`}>
                <div className={`conversations-list ${isMobile && selectedConversation ? "mobile-hidden" : ""}`}>
                    <div className="conversations-header">Messages</div>
                    {conversations.length === 0 && <p className="muted">No conversations yet.</p>}
                    {conversations.map((c) => {
                        const otherId = c.initiator_id === user.id ? c.recipient_id : c.initiator_id;
                        const otherName = nameMap[otherId] || `User ${otherId?.slice(0, 8) || ""}`;
                        const unreadCount = unreadMap[c.id] || 0;
                        const lastMsg = lastMessageMap[c.id];
                        return (
                            <button
                                key={c.id}
                                className={`conversation-pill ${c.id === selectedId ? "active" : ""} ${unreadCount > 0 ? "unread" : ""}`}
                                onClick={async () => {
                                    setSelectedId(c.id);
                                    await loadMessages(c.id);
                                }}
                            >
                                <div className="pill-content">
                                    <div className="pill-header">
                                        <span className="pill-name">{otherName}</span>
                                        <span className="pill-meta">
                                            {unreadCount > 0 && <span className="unread-dot" aria-label="unread" />}
                                            {c.status !== "accepted" && <span className={`status ${c.status}`}>{c.status}</span>}
                                        </span>
                                    </div>
                                    {lastMsg && (
                                        <div className="pill-preview">
                                            <span className="preview-sender">{lastMsg.sender_id === user.id ? "You" : otherName.split(" ")[0]}:</span>
                                            <span className="preview-text">{extractSharedPostId(lastMsg.body) ? "Shared a post" : formatMessageBody(lastMsg.body)}</span>
                                        </div>
                                    )}
                                </div>
                            </button>
                        );
                    })}
                </div>
                <div className={`messages-panel ${isMobile && !selectedConversation ? "mobile-hidden" : ""}`}>
                    {!selectedConversation ? (
                        <p className="muted">Select a conversation to view messages.</p>
                    ) : (
                        <>
                            <div className="messages-header">
                                {isMobile && (
                                    <button
                                        type="button"
                                        className="messages-back-btn"
                                        onClick={() => setSelectedId(null)}
                                    >
                                        Back
                                    </button>
                                )}
                                <div>
                                    <h2 className="conversation-title">
                                        {nameMap[selectedConversation.initiator_id === user.id ? selectedConversation.recipient_id : selectedConversation.initiator_id] || "User"}
                                    </h2>
                                    {selectedConversation.status !== "accepted" && (
                                        <div className="status-row">
                                            <span className={`status ${selectedConversation.status}`}>
                                                {selectedConversation.status}
                                            </span>
                                        </div>
                                    )}
                                </div>
                                {isPending && isRecipient && (
                                    <div className="pending-actions">
                                        <button
                                            className="btn-primary-msg"
                                            onClick={async () => {
                                                const { data } = await acceptConversation(selectedConversation.id);
                                                if (data) {
                                                    await refreshConversations(user.id);
                                                    await loadMessages(selectedConversation.id);
                                                }
                                            }}
                                        >Accept</button>
                                        <button
                                            className="btn-secondary"
                                            onClick={async () => {
                                                const { data } = await declineConversation(selectedConversation.id);
                                                if (data) {
                                                    await refreshConversations(user.id);
                                                    setMessages([]);
                                                }
                                            }}
                                        >Decline</button>
                                    </div>
                                )}
                            </div>
                            <div className="messages-list" ref={messagesListRef}>
                                {messages.length === 0 && <p className="muted">No messages yet.</p>}
                                {messages.map((m) => {
                                    const otherId = selectedConversation.initiator_id === user.id ? selectedConversation.recipient_id : selectedConversation.initiator_id;
                                    const senderName = m.sender_id === user.id ? "Me" : (nameMap[otherId] || "User");
                                    const sharedPostId = extractSharedPostId(m.body);
                                    const sharedPost = sharedPostId ? sharedPostMap[sharedPostId] : null;
                                    const displayBody = formatMessageBody(m.body);
                                    const mediaPreview = sharedPost?.media?.find((item) => item?.url) || null;
                                    return (
                                        <div key={m.id} className={`msg ${m.sender_id === user.id ? "out" : "in"}`}>
                                            <div className="msg-sender">{senderName}</div>
                                            {sharedPostId ? (
                                                <div
                                                    className="shared-post-card shared-post-card-clickable"
                                                    role="button"
                                                    tabIndex={0}
                                                    aria-label="Open shared post in home feed"
                                                    onClick={() => navigate("/home", {
                                                        state: {
                                                            focusPostId: sharedPostId,
                                                            focusPostPreview: sharedPost
                                                                ? {
                                                                    id: sharedPostId,
                                                                    author: sharedPost.authorName || "User",
                                                                    body: sharedPost.body || "",
                                                                    media: sharedPost.media || [],
                                                                    created_at: sharedPost.created_at,
                                                                }
                                                                : null,
                                                        }
                                                    })}
                                                    onKeyDown={(event) => {
                                                        if (event.key === "Enter" || event.key === " ") {
                                                            navigate("/home", {
                                                                state: {
                                                                    focusPostId: sharedPostId,
                                                                    focusPostPreview: sharedPost
                                                                        ? {
                                                                            id: sharedPostId,
                                                                            author: sharedPost.authorName || "User",
                                                                            body: sharedPost.body || "",
                                                                            media: sharedPost.media || [],
                                                                            created_at: sharedPost.created_at,
                                                                        }
                                                                        : null,
                                                                }
                                                            });
                                                        }
                                                    }}
                                                >
                                                    <div className="shared-post-card-header">Shared Post</div>
                                                    <div className="shared-post-card-author">{sharedPost?.authorName || "Loading author..."}</div>
                                                    {mediaPreview && (
                                                        mediaPreview.media_type === "video" ? (
                                                            <video className="shared-post-card-media" src={mediaPreview.url} controls preload="metadata" />
                                                        ) : (
                                                            <img className="shared-post-card-media" src={mediaPreview.url} alt="Shared post media" />
                                                        )
                                                    )}
                                                    <p className="shared-post-card-body">{sharedPost?.body || displayBody || "Shared a post"}</p>
                                                </div>
                                            ) : (
                                                <p>{displayBody}</p>
                                            )}
                                            <span className="meta">
                                                {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                                                {m.read_at && m.sender_id === user.id && " · Seen"}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                            {selectedConversation.status === "accepted" && (
                                <div className="composer">
                                    <textarea
                                        value={composer}
                                        onChange={(e) => setComposer(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter" && !e.shiftKey) {
                                                e.preventDefault();
                                                handleSend();
                                            }
                                        }}
                                        placeholder="Type a message..."
                                        rows={2}
                                    />
                                    <button className="btn-primary-msg" onClick={handleSend}>Send</button>
                                </div>
                            )}
                            {selectedConversation.status === "pending" && !isRecipient && (
                                <p className="muted">Waiting for the other user to accept.</p>
                            )}
                            {selectedConversation.status === "declined" && (
                                <p className="muted">Conversation declined.</p>
                            )}
                        </>
                    )}
                </div>
            </div>
        </main>
    );
}
